<template>
    <Head title="Redemption Tracker" />
    <div
        class="px-4 md:px-8 md:pt-10 transition-transform"
        :class="{
            'translate-x-0': !showRedemptionPanel,
            '-translate-x-[540px]': showRedemptionPanel,
        }"
    >
        <!-- Cover image with desktop stats overlay -->
        <TripCoverImage
            :trip="activeTrip"
            :trip-image="tripImage"
            :show-image-upload="showImageUpload"
            :loading="loading"
            :is-updating="isUpdating"
            :has-trip-image="hasTripImage"
            :trip-destination="tripDestination"
            @image-selected="handleImageSelected"
            @save="saveCoverImage"
            @close-upload="closeImageUpload"
            @open-upload="openImageUpload"
            @regenerate="regenerateCover"
            @cancel-changes="cancelCoverChanges"
            @delete-cover="showDeleteTripCoverConfirm = true"
        />

        <!-- Trip header with title, dates, actions -->
        <TripHeader
            :trip="activeTrip"
            @share="openShareModal"
            @add-redemption="tripsStore.setShowRedemptionCreateModal(true)"
        />

        <div
            class="flex justify-between items-center mb-2 sm:mb-0 mt-10 sm:mt-0"
            :class="{ '!mt-8': showEmailImportBanner && !loading }"
        >
            <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium">Redemptions</h3>
            <button
                v-if="isSubscribed"
                @click="showLearnMoreModal = true"
                class="flex items-center gap-1.5 text-sm font-medium text-[#1E293B] hover:text-slate-600 dark:text-slate-300 rounded"
            >
                <Icon
                    icon="email-forward"
                    :width="16"
                    :height="16"
                    pathClass="text-slate-800 dark:text-slate-100"
                />
                Import from email
            </button>
            <button
                v-else
                @click="showLearnMoreModal = true"
                class="flex items-center gap-1.5 text-sm font-medium text-[#1E293B] hover:text-slate-600 dark:text-slate-300 rounded"
            >
                <Icon
                    icon="email-forward"
                    :width="16"
                    :height="16"
                    pathClass="text-slate-800 dark:text-slate-100"
                />
                Upgrade to import from email
            </button>
        </div>

        <!-- Mobile stats -->
        <TripStatsOverlay :trip="activeTrip" variant="mobile" />

        <!-- Redemption list -->
        <div class="flex-col gap-5 relative mt-4 sm:mt-0">
            <RedemptionDateSegment
                v-for="dateSegment in redemptions"
                :date-segment="dateSegment"
                :key="dateSegment"
            />
        </div>

        <EmptyState
            v-if="redemptions.length === 0 && showEmptyRedemptionsState"
            class="mb-10"
            :icon="'squiggle'"
            title="No Redemptions yet"
            description="Add your first redemption to plan your trip and track points usage."
        >
            <template #action>
                <button
                    class="btn btn-secondary"
                    @click="tripsStore.setShowRedemptionCreateModal(true)"
                >
                    &plus; Add Redemption
                </button>
            </template>
        </EmptyState>

        <!-- All modals -->
        <TripModals
            :trip="activeTrip"
            :show-share-modal="showShareModal"
            :share-token="shareToken"
            :loading="loading"
            :show-delete-trip-confirm="showDeleteTripConfirm"
            :show-delete-trip-cover-confirm="showDeleteTripCoverConfirm"
            :delete-redemption-subtitle="deleteRedemptionSubtitle"
            :delete-redemption-dialog-text="deleteRedemptionDialogText"
            :show-learn-more-modal="showLearnMoreModal"
            :show-upgrade-modal="showUpgradeModal"
            :is-subscribed="isSubscribed"
            :user-email="userData?.email || 'your email'"
            @close-share="closeShareModal"
            @cancel-delete-trip-cover="showDeleteTripCoverConfirm = false"
            @confirm-clear-image="clearImage"
            @close-learn-more="showLearnMoreModal = false"
            @close-upgrade="showUpgradeModal = false"
        />

        <LoadingState :loading="loading" />
    </div>
</template>

<script setup>
import { Head, usePage, router } from '@inertiajs/vue3';

import { ref, onBeforeMount, onMounted, computed, watch } from 'vue';
import dayjs from 'dayjs';
import { notify } from 'notiwind';

import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import { useTripStats } from '@/composables/useTripStats';

import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import RedemptionDateSegment from '@/components/trips/redemptions/redemption-date-segment.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import Icon from '@/components/ui/icon/icon.vue';

import TripCoverImage from './trip/TripCoverImage.vue';
import TripHeader from './trip/TripHeader.vue';
import TripStatsOverlay from './trip/TripStatsOverlay.vue';
import TripModals from './trip/TripModals.vue';

const tripsStore = useTripsStore();
const showImageUpload = ref(false);
const showShareModal = ref(false);
const isUpdating = ref(false);
const originalImageCover = ref(null);
const showDeleteTripConfirm = ref(false);
const showDeleteTripCoverConfirm = ref(false);

const props = defineProps({ uuid: String });

const {
    activeTrip,
    loading,
    redemptions,
    showEmptyRedemptionsState,
    showRedemptionPanel,
    activeRedemption,
    upcomingTrips,
    pastTrips,
} = storeToRefs(tripsStore);

const { cdnUrl, formatCurrency } = useTripStats(activeTrip);

const shareToken = computed(() => activeTrip.value?.share_token);
const hasTripImage = computed(() => !!activeTrip.value?.image?.path);

const userData = computed(() => usePage().props.userData);
const isSubscribed = computed(() => userData.value?.is_subscribed ?? false);
const bannerDismissed = ref(false);
const dismissingBanner = ref(false);
const showLearnMoreModal = ref(false);
const showUpgradeModal = ref(false);

const showEmailImportBanner = computed(() => {
    return (
        !bannerDismissed.value && !userData.value?.email_import_banner_dismissed
    );
});

const dismissEmailImportBanner = async () => {
    dismissingBanner.value = true;
    bannerDismissed.value = true;
    try {
        await ApiService.apiCall('/user/user-setting', 'put', {
            key: 'email_import_banner_dismissed',
            value: true,
        });
    } catch (error) {
        // Silently fail — banner stays hidden for this session
    } finally {
        dismissingBanner.value = false;
    }
};

const tripImage = computed(() => {
    if (!activeTrip.value.image) return null;
    if (
        typeof activeTrip.value.image === 'string' &&
        activeTrip.value.image.startsWith('http')
    ) {
        return activeTrip.value.image;
    }
    return `${cdnUrl.value}/${activeTrip.value.image.path}/${activeTrip.value.image.file_name}`;
});

const tripDestination = computed({
    get() {
        const city = activeTrip.value.city || '';
        let state = activeTrip.value.state || '';
        if (state) {
            state = ' ' + state;
        }
        const country = activeTrip.value.country;
        return {
            formattedAddress:
                (state || city) && country
                    ? `${city}${state}, ${country}`
                    : null,
            city,
            state,
            country,
            valueForSearch: `${city.trim()}+${state.trim()}+${country?.trim()}`
                .replace(/,/g, '')
                .replace(/ /g, '+')
                .replace(/^\+/, '')
                .replace(/\+$/, '')
                .replace(/\+\+/, '+')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, ''),
        };
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, destination: value });
    },
});

// Image management
const regenerateCover = async () => {
    // Keep the whole cover from before the first regenerate (a media object or
    // a URL string) so Cancel can put it back (SUPPORT-835).
    if (!isUpdating.value) {
        originalImageCover.value = activeTrip.value.image;
    }
    isUpdating.value = true;
    try {
        const response = await ApiService.apiCall(
            `/media/random-image/${tripDestination.value.valueForSearch}`,
            'get'
        );
        activeTrip.value = {
            ...activeTrip.value,
            image: response.data.images.regular,
        };
    } catch (error) {
        notify({
            group: 'error',
            title: 'Error',
            text: 'Failed to regenerate cover image',
        });
    }
};

const clearImage = () => {
    showImageUpload.value = true;
    activeTrip.value.image = null;
    tripsStore.updateTrip({ message: 'Trip cover image removed' });
    showDeleteTripCoverConfirm.value = false;
};

const closeImageUpload = () => {
    showImageUpload.value = false;
};

const openImageUpload = () => {
    showImageUpload.value = true;
};

const cancelCoverChanges = () => {
    isUpdating.value = false;
    showImageUpload.value = false;
    activeTrip.value.image = originalImageCover.value;
    originalImageCover.value = null;
};

const handleImageSelected = async (imagePath) => {
    if (!activeTrip.value.id) {
        notify({
            group: 'error',
            title: 'Update Failed',
            text: 'Trip ID is missing. Cannot update the trip.',
        });
        return;
    }
    showImageUpload.value = true;
    activeTrip.value = { ...activeTrip.value, image: imagePath };
};

const saveCoverImage = async () => {
    try {
        await tripsStore.updateTrip({
            message: 'Trip cover image updated',
            ...activeTrip.value,
            image: activeTrip.value.image,
        });
        showImageUpload.value = false;
    } catch (error) {
        notify({
            group: 'error',
            title: 'Update Failed',
            text: 'Failed to update the trip with the new cover image.',
        });
        cancelCoverChanges();
    }
    isUpdating.value = false;
    showImageUpload.value = false;
};

// Share modal
const openShareModal = () => {
    showShareModal.value = true;
};

const closeShareModal = () => {
    showShareModal.value = false;
};

// Redemption details for delete dialog
const getCityName = (airport) => {
    if (!airport) return '';
    const match = airport.match(/^([^,(-]+)/);
    return match ? match[1].trim() : airport;
};

const formatDateRange = (start, end) => {
    if (!start) return '';
    const startDate = dayjs(start);
    if (!end || start === end) {
        return startDate.format('MMM D, YYYY');
    }
    const endDate = dayjs(end);
    if (startDate.year() === endDate.year()) {
        return `${startDate.format('MMM D')} – ${endDate.format('MMM D, YYYY')}`;
    }
    return `${startDate.format('MMM D, YYYY')} – ${endDate.format('MMM D, YYYY')}`;
};

const redemptionDetails = computed(() => {
    if (!activeRedemption.value) return '';
    const type = activeRedemption.value.redemption_type?.slug;
    let details = '';

    if (type === 'hotel') {
        const hotelName = activeRedemption.value.name || '';
        const dateRange = formatDateRange(
            activeRedemption.value.start_date,
            activeRedemption.value.end_date
        );
        details = hotelName
            ? `${hotelName}${dateRange ? `, ${dateRange}` : ''}`
            : '';
    } else if (type === 'flight' || type === 'train') {
        const segments = activeRedemption.value.travel_segments;
        if (segments && segments.length > 0) {
            const segment = segments[0];
            if (segment.origin && segment.destination) {
                const originCity = getCityName(
                    segment.origin_name || segment.origin
                );
                const destinationCity = getCityName(
                    segment.destination_name || segment.destination
                );
                const dateRange = formatDateRange(
                    segment.departure_date ||
                        activeRedemption.value.start_date,
                    segment.arrival_date || activeRedemption.value.end_date
                );
                const label = type === 'flight' ? 'Flight' : 'Train';
                details = `${label} ${originCity} – ${destinationCity}${dateRange ? `, ${dateRange}` : ''}`;
            }
        }
    } else if (type === 'rental-car') {
        const pickup = activeRedemption.value.pickup_location || '';
        const dropoff = activeRedemption.value.dropoff_location || '';
        const dateRange = formatDateRange(
            activeRedemption.value.start_date,
            activeRedemption.value.end_date
        );
        details = `Rental car ${pickup} – ${dropoff}${dateRange ? `, ${dateRange}` : ''}`;
    } else if (type === 'other') {
        const name = activeRedemption.value.name || '';
        const dateRange = formatDateRange(
            activeRedemption.value.start_date,
            activeRedemption.value.end_date
        );
        details = name
            ? `${name}${dateRange ? `, ${dateRange}` : ''}`
            : '';
    }

    return details;
});

const deleteRedemptionSubtitle = computed(() => redemptionDetails.value);
const deleteRedemptionDialogText = computed(
    () => 'You are about to delete this redemption. This action cannot be undone.'
);

// Lifecycle
onBeforeMount(() => {
    if (!props.uuid) {
        console.error('Trip UUID prop is missing or invalid');
        notify({
            group: 'error',
            title: 'Invalid Trip',
            text: 'The trip link appears to be invalid or corrupted. Please check the URL and try again.',
        });
        return;
    }
    tripsStore.getTrip(props.uuid);
    tripsStore.getRedemptions(props.uuid);
});

const findRedemptionById = (id) => {
    for (const segment of redemptions.value) {
        const found = segment.redemptions?.find((r) => r.id === id);
        if (found) return found;
    }
    return null;
};

onMounted(() => {
    if (!upcomingTrips.length && !pastTrips.length) useTripsStore().getTrips();

    const params = new URLSearchParams(window.location.search);
    const redemptionId = params.get('redemption');

    if (redemptionId) {
        const unwatch = watch(loading, (newVal, oldVal) => {
            if (oldVal === true && newVal === false) {
                const redemption = findRedemptionById(Number(redemptionId));
                if (redemption) {
                    tripsStore.setPanelRedemption(redemption);
                    tripsStore.setActiveRedemption(redemption);
                    tripsStore.setShowRedemptionPanel(true);
                }
                const url = new URL(window.location);
                url.searchParams.delete('redemption');
                window.history.replaceState({}, '', url);
                unwatch();
            }
        });
    }
});
</script>
